import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, Play, FileText, Shield, Lock, Video, Users2, Globe, 
  BookOpen, MessageSquare, Pen, ChevronDown, CheckCircle2,
  MapPin, Mail, User, Search, Edit, X, Eye, Save, Pause, Upload, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import * as perms from '../lib/permissions';

// Step 2 Sub-steps — keep step2a here (admin keeps the password field
// for the signup gate even though end-users no longer see step 2a as
// an interactive row).
const step2SubSteps = [
  { key: 'step2a_application_submitted', label: 'Submit Application', icon: FileText, description: 'Submit your application', route: '/submit-application', hasPassword: true, passwordKey: 'step2a' },
  { key: 'step2b_identity_verified', label: 'Verify your Identity', icon: Shield, description: 'Verify your identity', route: '/identity-verification' },
  { key: 'step2c_nda_signed', label: 'Sign NDA', icon: FileText, description: 'Sign NDA', route: '/nda', hasPassword: true, passwordKey: 'step2c' },
  { key: 'step2d_mfa_activated', label: 'Activate MFA', icon: Lock, description: 'Enable multi-factor authentication', route: '/mfa' },
  { key: 'step2e_leadership_interview', label: 'Undergo an Interview', icon: Video, description: 'We interview you', route: '/leadership-interview' },
  { key: 'step2h_how_orb_works', label: 'Learn how ORB works', icon: BookOpen, description: 'Training video', route: '/how-orb-works' },
  { key: 'step2i_role_duties', label: 'Learn the role & duties', icon: BookOpen, description: 'Training video', route: null },
  { key: 'step2j_live_qa', label: 'Interview our team', icon: MessageSquare, description: 'You interview us', route: null },
];

// All onboarding steps for candidate cards — drop "Step X:" prefixes.
const allOnboardingSteps = [
  { key: 'intro_video_watched', label: 'Watch Intro Video' },
  ...step2SubSteps.map((s) => ({ key: s.key, label: s.label })),
  { key: 'agreement_signed', label: 'Sign Agreement' }
];

// Default agreement configurations
const defaultAgreementConfig = {
  supervisor: { label: 'Supervisor', passwordKey: 'step3_supervisor', link: 'https://www.collectiveorb.com/supervisor-agreement' },
  affiliate: { label: 'Affiliate', passwordKey: 'step3_affiliate', link: 'https://www.collectiveorb.com/affiliate-equity-agreement' },
  lead_groups: [
    { states: ['North Carolina', 'South Carolina'], passwordKey: 'step3_lead_NC_SC', link: 'https://www.collectiveorb.com/affiliate-equity-agreement' },
    { states: ['California'], passwordKey: 'step3_lead_CA', link: 'https://www.collectiveorb.com/ca-agreement' },
    { states: ['New York'], passwordKey: 'step3_lead_NY', link: 'https://www.collectiveorb.com/ny-lead-agreement-signing' },
    { states: ['District of Columbia', 'Virginia'], passwordKey: 'step3_lead_DC_VA', link: 'https://www.collectiveorb.com/dc-va-agreement' },
    { states: ['Massachusetts'], passwordKey: 'step3_lead_MA', link: 'https://www.collectiveorb.com/ma-agreement' },
    { states: ['Illinois'], passwordKey: 'step3_lead_IL', link: 'https://www.collectiveorb.com/il-agreement' },
    { states: ['Pennsylvania', 'Maryland'], passwordKey: 'step3_lead_PA_MD', link: 'https://www.collectiveorb.com/pa-md-agreement' },
    { states: ['Georgia'], passwordKey: 'step3_lead_GA', link: 'https://www.collectiveorb.com/ga-agreement' },
    { states: ['Florida'], passwordKey: 'step3_lead_FL', link: 'https://www.collectiveorb.com/fl-agreement' },
    { states: ['Michigan', 'Ohio'], passwordKey: 'step3_lead_MI_OH', link: 'https://www.collectiveorb.com/mi-oh-agreement' },
    { states: ['Tennessee', 'Kentucky'], passwordKey: 'step3_lead_TN_KY', link: 'https://www.collectiveorb.com/tn-ky-agreement' },
    { states: ['Washington', 'Oregon'], passwordKey: 'step3_lead_WA_OR', link: 'https://www.collectiveorb.com/wa-or-agreement' },
    { states: ['Other States'], passwordKey: 'step3_lead_default', link: 'https://www.collectiveorb.com/ea-lead-agreement', isDefault: true }
  ]
};

const OnboardingAdminPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  usePageHeader({ title: 'Onboarding' }, []);
  const [mainTab, setMainTab] = useState('process');
  const [step2Expanded, setStep2Expanded] = useState(false);
  const [step3Expanded, setStep3Expanded] = useState(false);
  const [roleFilter, setRoleFilter] = useState('supervisor');
  const [candidates, setCandidates] = useState([]);
  // Users that were manually created via the admin Create-User flow.
  // Loaded only when the Admin Creations tab is opened.
  const [adminCreations, setAdminCreations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [passwords, setPasswords] = useState({});
  const [expandedCandidates, setExpandedCandidates] = useState({});
  const [editingCandidate, setEditingCandidate] = useState(null);
  
  // Password editing
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editingPasswordStep, setEditingPasswordStep] = useState(null);
  const [newAccessPassword, setNewAccessPassword] = useState('');
  const [newProgressPassword, setNewProgressPassword] = useState('');
  // iter164 — extended step-meta fields
  const [newStepLink, setNewStepLink] = useState('');
  const [newStepAccessEnabled, setNewStepAccessEnabled] = useState(true);
  const [newStepBgColor, setNewStepBgColor] = useState('');
  
  // Agreement preview
  const [showAgreementPreview, setShowAgreementPreview] = useState(false);
  const [previewAgreementUrl, setPreviewAgreementUrl] = useState('');
  const [previewAgreementTitle, setPreviewAgreementTitle] = useState('');
  const [previewAccessPassword, setPreviewAccessPassword] = useState('');
  
  // Video preview
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  

  // iter166 — Admin-attached videos for Step 1 (intro), Step 2H, Step 2I.
  // Schema: { intro_video: {url, title}, step2h_video: {url, title}, step2i_video: {url, title} }
  const [adminVideos, setAdminVideos] = useState({});
  const [uploadingSlot, setUploadingSlot] = useState(null);

  // iter175ca — Per-role onboarding overrides for the three Step 2
  // sub-steps that vary by account type (supervisor / lead /
  // affiliate). Each row carries an independent value per role.
  // Shape mirrors the backend:
  //   {
  //     leadership_interview_links: {supervisor, lead, affiliate},
  //     live_qa_links:              {supervisor, lead, affiliate},
  //     step2i_video: {
  //       supervisor: {url, title},
  //       lead:       {url, title},
  //       affiliate:  {url, title}
  //     }
  //   }
  const [roleOverrides, setRoleOverrides] = useState({
    leadership_interview_links: { supervisor: '', lead: '', affiliate: '' },
    live_qa_links: { supervisor: '', lead: '', affiliate: '' },
    step2i_video: {
      supervisor: { url: '', title: '' },
      lead: { url: '', title: '' },
      affiliate: { url: '', title: '' },
    },
  });
  const [uploadingRoleVideo, setUploadingRoleVideo] = useState(null);

  // Search input ref to maintain focus
  const searchInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  // Permission helpers (full admin = unrestricted; special user with the
  // `admin_onboarding` toggle = read-only on Process + Candidates and
  // hidden Admin Creations tab).
  const onb = perms.onboardingAdmin(user);
  const canAccessOnboarding = onb.view;

  useEffect(() => {
    if (!canAccessOnboarding) {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [canAccessOnboarding, navigate]);

  // Load passwords
  const loadPasswords = useCallback(async () => {
    try {
      const res = await api.get('/admin/onboarding/passwords');
      setPasswords(res.data);
    } catch (error) {
      console.error('Failed to load passwords:', error);
    }
  }, [api]);

  // Load candidates
  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/onboarding/candidates');
      setCandidates(res.data || []);
    } catch (error) {
      console.error('Failed to load candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Load admin-created users (separate endpoint so we get manually
  // provisioned accounts even if they're already onboarded).
  const loadAdminCreations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/onboarding/admin-creations');
      setAdminCreations(res.data || []);
    } catch (error) {
      console.error('Failed to load admin-created users:', error);
      toast.error('Failed to load admin-created users');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadPasswords();
  }, [loadPasswords]);

  // iter166 — Load admin-attached videos once on mount.
  const loadAdminVideos = useCallback(async () => {
    try {
      const res = await api.get('/admin/onboarding/videos');
      setAdminVideos(res.data || {});
    } catch (_e) { /* keep current */ }
  }, [api]);
  useEffect(() => {
    if (isAdmin) loadAdminVideos();
  }, [isAdmin, loadAdminVideos]);

  // iter175ca — Load per-role overrides for Step 2E/2I/2J once on mount.
  const loadRoleOverrides = useCallback(async () => {
    try {
      const res = await api.get('/admin/onboarding/role-overrides');
      if (res?.data) setRoleOverrides(res.data);
    } catch (_e) { /* keep defaults */ }
  }, [api]);
  useEffect(() => {
    if (isAdmin) loadRoleOverrides();
  }, [isAdmin, loadRoleOverrides]);

  // iter175ca — Save a single role-keyed override (link OR video).
  // Optimistic: update local state first, then POST. On failure the
  // server-side state simply rejects without rolling back the UI.
  const saveRoleOverrideLink = useCallback(async (kind, role, link) => {
    setRoleOverrides(prev => ({
      ...prev,
      [kind]: { ...(prev[kind] || {}), [role]: link },
    }));
    try {
      await api.put('/admin/onboarding/role-overrides', { kind, role, link });
    } catch (_e) {
      toast.error('Failed to save link');
    }
  }, [api]);

  const saveRoleOverrideVideo = useCallback(async (role, file) => {
    if (!file) return;
    if (!file.type?.startsWith('video/')) {
      toast.error('Please choose a video file');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Video is over 30 MB. Please compress or use external hosting.');
      return;
    }
    setUploadingRoleVideo(role);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.put('/admin/onboarding/role-overrides', {
        kind: 'step2i_video',
        role,
        url: dataUrl,
        title: file.name,
      });
      setRoleOverrides(prev => ({
        ...prev,
        step2i_video: {
          ...(prev.step2i_video || {}),
          [role]: { url: dataUrl, title: file.name },
        },
      }));
      toast.success('Video uploaded');
    } catch (_e) {
      toast.error('Upload failed');
    } finally {
      setUploadingRoleVideo(null);
    }
  }, [api]);

  const deleteRoleOverrideVideo = useCallback(async (role) => {
    try {
      await api.put('/admin/onboarding/role-overrides', {
        kind: 'step2i_video', role, url: '', title: '',
      });
      setRoleOverrides(prev => ({
        ...prev,
        step2i_video: {
          ...(prev.step2i_video || {}),
          [role]: { url: '', title: '' },
        },
      }));
      toast.success('Video removed');
    } catch (_e) {
      toast.error('Delete failed');
    }
  }, [api]);

  // iter166 — Upload a new video for the given slot. Reads the file
  // as a data URL (so it survives without external storage) and POSTs
  // it to the admin endpoint. Caller is the file-input change handler.
  const handleVideoUpload = useCallback(async (slot, file) => {
    if (!file) return;
    if (!file.type?.startsWith('video/')) {
      toast.error('Please choose a video file');
      return;
    }
    // Soft cap: 30 MB — anything larger should live in real storage.
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Video is over 30 MB. Please compress or use external hosting.');
      return;
    }
    setUploadingSlot(slot);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.put('/admin/onboarding/videos', { slot, url: dataUrl, title: file.name });
      toast.success('Video uploaded');
      loadAdminVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingSlot(null);
    }
  }, [api, loadAdminVideos]);

  const handleVideoDelete = useCallback(async (slot) => {
    if (!window.confirm('Remove this video? The default video will play instead.')) return;
    try {
      await api.put('/admin/onboarding/videos', { slot, url: null, title: '' });
      toast.success('Video removed');
      loadAdminVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove video');
    }
  }, [api, loadAdminVideos]);

  // iter166 — Compact upload/replace/delete control rendered inside
  // the Step 1 / Step 2H / Step 2I cards on the Process tab. Shows
  // whether a video is currently attached.
  const renderVideoControl = (slot, defaultStaticPath) => {
    const v = adminVideos[slot] || {};
    const hasCustom = !!v.url;
    const isUploading = uploadingSlot === slot;
    const inputId = `video-upload-${slot}`;
    return (
      <div
        className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2 flex-wrap"
        onClick={(e) => e.stopPropagation()}
        data-testid={`admin-video-control-${slot}`}
      >
        <div className="text-xs flex-1 min-w-0">
          <span className="text-muted-foreground">Current video:</span>{' '}
          <span className="font-medium truncate inline-block max-w-[200px] align-bottom" title={hasCustom ? (v.title || 'custom upload') : defaultStaticPath}>
            {hasCustom ? (v.title || 'Custom upload') : defaultStaticPath}
          </span>
        </div>
        <input
          id={inputId}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleVideoUpload(slot, f);
            e.target.value = '';
          }}
          data-testid={`admin-video-input-${slot}`}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          onClick={() => document.getElementById(inputId)?.click()}
          disabled={isUploading}
          data-testid={`admin-video-upload-btn-${slot}`}
        >
          <Upload className="w-3 h-3 mr-1" />
          {isUploading ? 'Uploading…' : (hasCustom ? 'Replace' : 'Add')}
        </Button>
        {hasCustom && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
            onClick={() => handleVideoDelete(slot)}
            disabled={isUploading}
            data-testid={`admin-video-delete-btn-${slot}`}
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Delete
          </Button>
        )}
      </div>
    );
  };

  // iter175ca — Per-role link inputs for Step 2E ("Undergo an
  // Interview") and Step 2J ("Interview our team"). Renders three
  // stacked text inputs (supervisor / lead / affiliate) inside the
  // Step 2 sub-step card. End-user UI is unchanged — these inputs
  // only override the URL the user hits when they click their
  // role-specific booking button.
  const renderRoleLinks = (kind, helpText) => {
    const values = roleOverrides?.[kind] || {};
    return (
      <div
        className="mt-3 pt-3 border-t border-border/60 space-y-2"
        onClick={(e) => e.stopPropagation()}
        data-testid={`admin-role-links-${kind}`}
      >
        <div className="text-xs text-muted-foreground">{helpText}</div>
        {['supervisor', 'lead', 'affiliate'].map((role) => (
          <div key={role} className="flex items-center gap-2">
            <label className="text-xs font-medium capitalize w-20 text-muted-foreground">
              {role}
            </label>
            <input
              type="url"
              className="flex-1 h-8 px-2 text-xs border border-border rounded-md bg-background"
              value={values[role] || ''}
              placeholder="https://outlook.office.com/book/..."
              onChange={(e) =>
                setRoleOverrides(prev => ({
                  ...prev,
                  [kind]: { ...(prev[kind] || {}), [role]: e.target.value },
                }))
              }
              onBlur={(e) => saveRoleOverrideLink(kind, role, e.target.value)}
              data-testid={`admin-role-link-${kind}-${role}`}
            />
          </div>
        ))}
      </div>
    );
  };

  // iter175ca — Per-role video upload controls for Step 2I ("Learn
  // the role & duties"). Three stacked uploaders matching the
  // existing `renderVideoControl` pattern but keyed by role.
  const renderRoleVideos = () => (
    <div
      className="mt-3 pt-3 border-t border-border/60 space-y-2"
      onClick={(e) => e.stopPropagation()}
      data-testid="admin-role-videos-step2i"
    >
      <div className="text-xs text-muted-foreground">
        Upload a different training video per account type. Leaving a role empty falls back to the global video above.
      </div>
      {['supervisor', 'lead', 'affiliate'].map((role) => {
        const v = (roleOverrides?.step2i_video || {})[role] || {};
        const hasCustom = !!v.url;
        const isUploading = uploadingRoleVideo === role;
        const inputId = `role-video-upload-${role}`;
        return (
          <div key={role} className="flex items-center gap-2 flex-wrap">
            <label className="text-xs font-medium capitalize w-20 text-muted-foreground">
              {role}
            </label>
            <div className="text-xs flex-1 min-w-0 truncate" title={hasCustom ? (v.title || 'custom upload') : 'none'}>
              {hasCustom ? (v.title || 'Custom upload') : <span className="text-muted-foreground italic">none</span>}
            </div>
            <input
              id={inputId}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) saveRoleOverrideVideo(role, f);
                e.target.value = '';
              }}
              data-testid={`admin-role-video-input-step2i-${role}`}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => document.getElementById(inputId)?.click()}
              disabled={isUploading}
              data-testid={`admin-role-video-upload-btn-step2i-${role}`}
            >
              <Upload className="w-3 h-3 mr-1" />
              {isUploading ? 'Uploading…' : (hasCustom ? 'Replace' : 'Add')}
            </Button>
            {hasCustom && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
                onClick={() => deleteRoleOverrideVideo(role)}
                disabled={isUploading}
                data-testid={`admin-role-video-delete-btn-step2i-${role}`}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );

  useEffect(() => {
    if (mainTab === 'candidates') {
      loadCandidates();
    } else if (mainTab === 'admin_creations') {
      loadAdminCreations();
    }
  }, [mainTab, loadCandidates, loadAdminCreations]);

  // Filter candidates by role and search.
  // iter164 — When a search query is present, the role filter is
  // ignored so the admin sees matches across ALL three role tabs
  // (supervisor / lead / affiliate). Per user: "I want it to search
  // users under all 3 tabs."
  const filteredCandidates = candidates.filter(c => {
    const q = (searchQuery || '').toLowerCase();
    if (q) {
      return (
        (c.full_name || '').toLowerCase().includes(q) ||
        (c.first_name || '').toLowerCase().includes(q) ||
        (c.last_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      );
    }
    return c.role === roleFilter;
  });

  // Get password for a step
  const getPassword = useCallback((passwordKey, type) => {
    return passwords[passwordKey]?.[type] || '';
  }, [passwords]);

  // Handle password update
  const handlePasswordUpdate = async () => {
    if (!editingPasswordStep) return;
    
    try {
      if (newAccessPassword) {
        await api.put('/admin/onboarding/passwords', {
          step_id: editingPasswordStep.passwordKey,
          password_type: 'access',
          new_password: newAccessPassword
        });
      }
      if (newProgressPassword) {
        await api.put('/admin/onboarding/passwords', {
          step_id: editingPasswordStep.passwordKey,
          password_type: 'progress',
          new_password: newProgressPassword
        });
      }
      // iter164 — also send link / access_enabled / bg_color via the
      // new step-meta endpoint. We always send all three so toggling
      // any one fires a single round-trip.
      await api.put('/admin/onboarding/step-meta', {
        step_id: editingPasswordStep.passwordKey,
        link: newStepLink,
        access_enabled: newStepAccessEnabled,
        bg_color: newStepBgColor,
      });
      
      toast.success('Saved');
      loadPasswords();
      setShowPasswordDialog(false);
      setNewAccessPassword('');
      setNewProgressPassword('');
      setNewStepLink('');
      setNewStepAccessEnabled(true);
      setNewStepBgColor('');
      setEditingPasswordStep(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save');
    }
  };

  // iter166 — Silent refresh of the candidate list. Used after
  // admin actions (mark step complete, save passwords, etc.) so we
  // get fresh data WITHOUT the global `setLoading(true)` jolt that
  // unmounts the candidate cards, collapses expanded rows, and
  // resets scroll position.
  const refreshCandidatesSilently = useCallback(async () => {
    try {
      const res = await api.get('/admin/onboarding/candidates');
      setCandidates(res.data || []);
    } catch (_e) { /* keep current list on failure */ }
  }, [api]);

  // Handle step completion update
  const handleStepUpdate = async (candidateId, stepKey) => {
    // Optimistic UI: flip the flag locally first so the checkbox
    // turns green immediately. The silent refresh below replaces
    // the candidate object with the server's view a moment later
    // (also pulls any cascading changes like is_onboarded).
    setCandidates((list) =>
      list.map((c) => (c.id === candidateId ? { ...c, [stepKey]: true } : c)),
    );
    try {
      await api.put(`/admin/users/${candidateId}/onboarding-step`, {
        step_key: stepKey,
        completed: true,
      });
      toast.success('Step marked as complete');
      refreshCandidatesSilently();
    } catch (error) {
      // Revert the optimistic flip on failure.
      setCandidates((list) =>
        list.map((c) => (c.id === candidateId ? { ...c, [stepKey]: false } : c)),
      );
      toast.error(error.response?.data?.detail || 'Failed to update step');
    }
  };

  // Get candidate progress
  const getCandidateProgress = useCallback((candidate) => {
    const completed = allOnboardingSteps.filter(s => candidate[s.key]).length;
    return Math.round((completed / allOnboardingSteps.length) * 100);
  }, []);

  const getInitials = useCallback((name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  }, []);

  // Open password edit dialog
  const openPasswordEdit = useCallback((step) => {
    setEditingPasswordStep(step);
    setNewAccessPassword(getPassword(step.passwordKey, 'access'));
    setNewProgressPassword(getPassword(step.passwordKey, 'progress'));
    // iter164 — read existing step-meta from the same subdoc
    const meta = passwords?.[step.passwordKey] || {};
    setNewStepLink(meta.link || step.defaultLink || '');
    setNewStepAccessEnabled(meta.access_enabled !== false); // default ON
    setNewStepBgColor(meta.bg_color || '');
    setShowPasswordDialog(true);
  }, [getPassword, passwords]);

  // Open agreement preview
  const openAgreementPreview = useCallback((url, title, accessPassword) => {
    setPreviewAgreementUrl(url);
    setPreviewAgreementTitle(title);
    setPreviewAccessPassword(accessPassword);
    setShowAgreementPreview(true);
  }, []);

  // Handle search input change - prevent re-render issues
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // Handle back navigation - go to admin/onboarding if coming from preview
  const handleBack = useCallback(() => {
    navigate('/admin/onboarding');
  }, [navigate]);

  if (!canAccessOnboarding) return null;

  // Intro Video Player Component
  const IntroVideoPlayer = () => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    }, []);

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const remainingTime = Math.max(0, duration - currentTime);

    return (
      <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center">
        <div className="relative w-full max-w-md mx-4">
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <Button
              variant="ghost"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
              onClick={() => {
                if (videoRef.current) {
                  if (isPlaying) {
                    videoRef.current.pause();
                  } else {
                    videoRef.current.play();
                  }
                  setIsPlaying(!isPlaying);
                }
              }}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
              onClick={() => setShowVideoPreview(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {duration > 0 && (
            <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
              <span className="text-white text-sm font-medium">
                {formatTime(remainingTime)} remaining
              </span>
            </div>
          )}
          
          <video
            ref={videoRef}
            className="w-full rounded-lg"
            onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
            onLoadedMetadata={() => videoRef.current && setDuration(videoRef.current.duration)}
            playsInline
          >
            <source src="/intro_video.mp4" type="video/mp4" />
          </video>
          
          <p className="text-center text-white mt-4 text-sm">
            Admin Preview: Intro Video (Step 1)
          </p>
        </div>
      </div>
    );
  };

  // Agreement Preview Modal
  const AgreementPreviewModal = () => {
    if (!showAgreementPreview) return null;
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #dfdfdf',
            backgroundColor: '#ffffff',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText style={{ width: '20px', height: '20px', color: '#757150' }} />
            <div>
              <span style={{ fontWeight: 600, color: '#1c1a1c' }}>
                {previewAgreementTitle}
              </span>
              <p style={{ fontSize: '12px', color: '#676767', margin: 0 }}>
                Password: <code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '4px' }}>{previewAccessPassword}</code>
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => setShowAgreementPreview(false)}
            className="gap-2"
          >
            <X className="w-4 h-4" />
            Exit Preview
          </Button>
        </div>
        
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <iframe
            src={previewAgreementUrl}
            title={previewAgreementTitle}
            allow="camera; microphone"
            scrolling="yes"
            style={{
              width: '73.5%',
              height: '73.5%',
              border: 'none',
              display: 'block',
              transform: 'scale(1.36)',
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </div>
      </div>
    );
  };

  // Password Edit Dialog
  // iter166 — Rendered as JSX (NOT a sub-component) so inputs inside
  // it don't lose focus on every keystroke. Defining the dialog as
  // `const X = () => (...)` inside the parent caused React to treat
  // it as a new component type on every render of the parent (which
  // happens on every input change), unmounting and remounting the
  // whole subtree and yanking focus from the active <input>.
  const passwordEditDialog = (
    <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Passwords</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {editingPasswordStep?.label}
          </p>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* iter164 — Access password row with the new toggle.
              When the toggle is OFF, the user-side popup MUST NOT
              render the password gate on the page-before. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm">Access Password</Label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={newStepAccessEnabled}
                  onChange={(e) => setNewStepAccessEnabled(e.target.checked)}
                  data-testid="access-password-enabled-toggle"
                />
                <span>{newStepAccessEnabled ? 'Required' : 'Not required'}</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Shown on the page before the popup</p>
            <Input
              value={newAccessPassword}
              onChange={(e) => setNewAccessPassword(e.target.value)}
              placeholder="Enter access password"
              disabled={!newStepAccessEnabled}
              data-testid="step-access-password-input"
            />
          </div>

          <div>
            <Label className="text-sm">Progress Password</Label>
            <p className="text-xs text-muted-foreground mb-2">Users enter this to advance</p>
            <Input
              value={newProgressPassword}
              onChange={(e) => setNewProgressPassword(e.target.value)}
              placeholder="Enter progress password"
              data-testid="step-progress-password-input"
            />
          </div>

          {/* iter164 — Per-step URL the popup opens. */}
          <div>
            <Label className="text-sm">URL Link</Label>
            <p className="text-xs text-muted-foreground mb-2">Wraps inside the popup for this step</p>
            <Input
              value={newStepLink}
              onChange={(e) => setNewStepLink(e.target.value)}
              placeholder="https://docs.example.com/agreement"
              data-testid="step-link-input"
            />
          </div>

          {/* iter164 — Per-step background color shown behind the
              popup. Hex code. Defaults to a translucent dark blur.
              iter166 — Use a native color picker as the primary
              control so the swatch can't "flash" between valid /
              invalid partial hex values while the admin types.
              The text input still accepts manual hex entry but is
              debounced to a valid value when used. */}
          <div>
            <Label className="text-sm">Background Color</Label>
            <p className="text-xs text-muted-foreground mb-2">Solid color rendered behind the popup</p>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(newStepBgColor) ? newStepBgColor : '#cccccc'}
                onChange={(e) => setNewStepBgColor(e.target.value)}
                className="h-10 w-12 rounded border cursor-pointer p-0 bg-transparent"
                data-testid="step-bg-color-picker"
                aria-label="Background color picker"
              />
              <Input
                value={newStepBgColor}
                onChange={(e) => setNewStepBgColor(e.target.value)}
                placeholder="#0F2A1A"
                className="flex-1 font-mono"
                data-testid="step-bg-color-input"
              />
              {newStepBgColor && (
                <button
                  type="button"
                  onClick={() => setNewStepBgColor('')}
                  className="text-xs text-muted-foreground hover:text-foreground px-2"
                  data-testid="step-bg-color-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1" onClick={() => setShowPasswordDialog(false)}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handlePasswordUpdate}>
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Edit Candidate Dialog
  const EditCandidateDialog = () => {
    if (!editingCandidate) return null;
    
    return (
      <Dialog open={!!editingCandidate} onOpenChange={() => setEditingCandidate(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Onboarding Steps
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {editingCandidate.full_name} ({editingCandidate.email})
            </p>
          </DialogHeader>
          
          <div className="space-y-2 mt-4">
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
              Note: You can only mark incomplete steps as complete.
            </p>
            
            {allOnboardingSteps.map((step) => {
              const isComplete = editingCandidate[step.key];
              
              return (
                <div 
                  key={step.key}
                  className={`flex items-center gap-3 p-3 rounded-lg ${isComplete ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-800'}`}
                >
                  <Checkbox
                    checked={isComplete}
                    disabled={isComplete}
                    onCheckedChange={(checked) => {
                      if (checked && !isComplete) {
                        handleStepUpdate(editingCandidate.id, step.key);
                        setEditingCandidate(prev => ({...prev, [step.key]: true}));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <p className={`text-sm ${isComplete ? 'text-green-700 dark:text-green-400' : ''}`}>
                      {step.label}
                    </p>
                    {isComplete && editingCandidate[`${step.key}_at`] && (
                      <p className="text-xs text-muted-foreground">
                        Completed: {formatDate(editingCandidate[`${step.key}_at`])}
                      </p>
                    )}
                  </div>
                  {isComplete && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                </div>
              );
            })}
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setEditingCandidate(null)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="onboarding-admin-page">
      {showVideoPreview && <IntroVideoPlayer />}
      <AgreementPreviewModal />
      {passwordEditDialog}
      <EditCandidateDialog />
      
      <div className="max-w-2xl mx-auto">
        {/* Tab Toggle */}
        <div className="flex bg-gray-100 dark:bg-[#2a2a2a] rounded-lg p-1 mb-6 gap-0.5">
          <button
            onClick={() => setMainTab('process')}
            className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              mainTab === 'process' ? 'bg-white dark:bg-[#1a1a1a] shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-process"
          >
            Process
          </button>
          <button
            onClick={() => setMainTab('candidates')}
            className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              mainTab === 'candidates' ? 'bg-white dark:bg-[#1a1a1a] shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-candidates"
          >
            Candidates
          </button>
          {onb.viewAdminCreations && (
            <button
              onClick={() => setMainTab('admin_creations')}
              className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                mainTab === 'admin_creations' ? 'bg-white dark:bg-[#1a1a1a] shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-admin-creations"
            >
              Admin Creations
            </button>
          )}
        </div>

        {/* PROCESS TAB */}
        {mainTab === 'process' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Preview all onboarding steps exactly as candidates see them.
            </p>
            
            {/* Step 1 */}
            <Card 
              className="transition-all hover:shadow-md"
              data-testid="admin-onboarding-step-1"
            >
              <CardContent className="p-4">
                <div
                  className="flex items-center gap-4 cursor-pointer"
                  onClick={() => setShowVideoPreview(true)}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                    <Play className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Step 1: Watch Intro Video</h3>
                    <p className="text-sm text-muted-foreground">Learn about The Uplift</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setShowVideoPreview(true); }}>
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Button>
                </div>
                {renderVideoControl('intro_video', '/intro_video.mp4')}
              </CardContent>
            </Card>

            {/* Step 2 */}
            <div>
              <Card 
                className="transition-all hover:shadow-md cursor-pointer"
                onClick={() => setStep2Expanded(!step2Expanded)}
                data-testid="admin-onboarding-step-2"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">Step 2: Complete Onboarding</h3>
                      <p className="text-sm text-muted-foreground">{step2SubSteps.length} sub-steps</p>
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform ${step2Expanded ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>

              {step2Expanded && (
                <div className="ml-6 mt-2 space-y-2 border-l-2 border-primary/20 pl-4">
                  {step2SubSteps.map((subStep, subIdx) => {
                    const SubIcon = subStep.icon;
                    const progressPwd = subStep.hasPassword ? getPassword(subStep.passwordKey, 'progress') : null;
                    
                    return (
                      <Card key={subStep.key} className="transition-all">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary flex-shrink-0">
                              <SubIcon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">Step 2{String.fromCharCode(65 + subIdx)}: {subStep.label}</p>
                              <p className="text-xs text-muted-foreground">{subStep.description}</p>
                              
                              {subStep.hasPassword && (
                                <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">Progress Password:</span>
                                    <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{progressPwd || 'Not set'}</code>
                                  </div>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="w-full text-xs h-7"
                                    onClick={(e) => { e.stopPropagation(); openPasswordEdit(subStep); }}
                                  >
                                    <Edit className="w-3 h-3 mr-1" /> Change Password
                                  </Button>
                                </div>
                              )}
                            </div>
                            {subStep.route && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs flex-shrink-0" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  sessionStorage.setItem('admin_onboarding_return', 'true');
                                  navigate(subStep.route, { state: { fromAdmin: true } }); 
                                }}
                              >
                                Preview
                              </Button>
                            )}
                          </div>
                          {/* iter166 — Video controls for the two
                              training-video sub-steps (2H/2I in the
                              displayed lettering = step2h/step2i in code). */}
                          {subStep.key === 'step2h_how_orb_works' && renderVideoControl('step2h_video', '/step2h_video.mp4')}
                          {subStep.key === 'step2i_role_duties' && renderVideoControl('step2i_video', '/step2i_video.mp4 (not yet uploaded)')}
                          {/* iter175ca — Per-role overrides. End-user UI
                              is unchanged — the role-keyed values just
                              redirect the user's click target. */}
                          {subStep.key === 'step2e_leadership_interview' && renderRoleLinks(
                            'leadership_interview_links',
                            'Booking link per account type. Each user sees only the link for their own role.'
                          )}
                          {subStep.key === 'step2i_role_duties' && renderRoleVideos()}
                          {subStep.key === 'step2j_live_qa' && renderRoleLinks(
                            'live_qa_links',
                            'Booking link per account type. Each user sees only the link for their own role.'
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Step 3 */}
            <div>
              <Card 
                className="transition-all hover:shadow-md cursor-pointer"
                onClick={() => setStep3Expanded(!step3Expanded)}
                data-testid="admin-onboarding-step-3"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                      <Pen className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">Step 3: Sign Agreement</h3>
                      <p className="text-sm text-muted-foreground">Role-specific agreements</p>
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform ${step3Expanded ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>

              {step3Expanded && (
                <div className="ml-6 mt-2 space-y-3 border-l-2 border-primary/20 pl-4">
                  {/* Supervisors */}
                  <Card>
                    <CardContent className="p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">All Supervisors</p>
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => openAgreementPreview(
                            defaultAgreementConfig.supervisor.link, 
                            'Supervisor Agreement',
                            getPassword('step3_supervisor', 'access')
                          )}>
                            <Eye className="w-3 h-3 mr-1" /> View Agreement
                          </Button>
                        </div>
                        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Access Password:</span>
                            <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{getPassword('step3_supervisor', 'access') || 'Not set'}</code>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Progress Password:</span>
                            <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{getPassword('step3_supervisor', 'progress') || 'Not set'}</code>
                          </div>
                          <Button variant="ghost" size="sm" className="w-full text-xs h-7 mt-1" onClick={() => openPasswordEdit({ passwordKey: 'step3_supervisor', label: 'Supervisors' })}>
                            <Edit className="w-3 h-3 mr-1" /> Change Passwords
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Affiliates */}
                  <Card>
                    <CardContent className="p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">All Affiliates</p>
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => openAgreementPreview(
                            defaultAgreementConfig.affiliate.link, 
                            'Affiliate Agreement',
                            getPassword('step3_affiliate', 'access')
                          )}>
                            <Eye className="w-3 h-3 mr-1" /> View Agreement
                          </Button>
                        </div>
                        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Access Password:</span>
                            <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{getPassword('step3_affiliate', 'access') || 'Not set'}</code>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Progress Password:</span>
                            <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{getPassword('step3_affiliate', 'progress') || 'Not set'}</code>
                          </div>
                          <Button variant="ghost" size="sm" className="w-full text-xs h-7 mt-1" onClick={() => openPasswordEdit({ passwordKey: 'step3_affiliate', label: 'Affiliates' })}>
                            <Edit className="w-3 h-3 mr-1" /> Change Passwords
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Lead Groups */}
                  {defaultAgreementConfig.lead_groups.map((group, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">{group.states.join(' & ')} Leads</p>
                            <Button variant="outline" size="sm" className="text-xs" onClick={() => openAgreementPreview(
                              group.link, 
                              'Lead Agreement',
                              getPassword(group.passwordKey, 'access')
                            )}>
                              <Eye className="w-3 h-3 mr-1" /> View Agreement
                            </Button>
                          </div>
                          <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">Access Password:</span>
                              <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{getPassword(group.passwordKey, 'access') || 'Not set'}</code>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">Progress Password:</span>
                              <code className="text-xs bg-white dark:bg-gray-700 px-2 py-0.5 rounded">{getPassword(group.passwordKey, 'progress') || 'Not set'}</code>
                            </div>
                            <Button variant="ghost" size="sm" className="w-full text-xs h-7 mt-1" onClick={() => openPasswordEdit({ passwordKey: group.passwordKey, label: `${group.states.join(' & ')} Leads` })}>
                              <Edit className="w-3 h-3 mr-1" /> Change Passwords
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CANDIDATES TAB */}
        {mainTab === 'candidates' && (
          <div className="space-y-4">
            {/* Search Bar - uncontrolled input to prevent iOS keyboard issues */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by name..."
                defaultValue={searchQuery}
                onInput={(e) => {
                  // Use a debounced approach to prevent re-renders during typing
                  const value = e.target.value;
                  // Update search after a tiny delay to avoid interrupting typing
                  if (window.searchTimeout) clearTimeout(window.searchTimeout);
                  window.searchTimeout = setTimeout(() => setSearchQuery(value), 100);
                }}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="candidate-search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>

            {/* Role Filter Buttons */}
            <div className="flex gap-2">
              {['supervisor', 'lead', 'affiliate'].map((role) => {
                const isActive = roleFilter === role;
                const count = candidates.filter(c => c.role === role).length;
                return (
                  <Button
                    key={role}
                    variant={isActive ? 'default' : 'outline'}
                    onClick={() => setRoleFilter(role)}
                    className={`flex-1 ${isActive ? 'bg-[#586D52] hover:bg-[#4a5d46]' : ''}`}
                    data-testid={`filter-${role}`}
                  >
                    {role.charAt(0).toUpperCase() + role.slice(1)}s ({count})
                  </Button>
                );
              })}
            </div>

            {/* Candidates List */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4 h-24 bg-muted/50" />
                  </Card>
                ))}
              </div>
            ) : filteredCandidates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <User className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {searchQuery ? 'No candidates match your search' : `No ${roleFilter} candidates in onboarding`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredCandidates.map((candidate) => {
                  const progress = getCandidateProgress(candidate);
                  const completedCount = allOnboardingSteps.filter(s => candidate[s.key]).length;
                  const isExpanded = expandedCandidates[candidate.id];
                  
                  return (
                    <Card key={candidate.id} className="hover:shadow-md transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-12 w-12 flex-shrink-0">
                            <AvatarFallback className="bg-[#586D52]/20 text-[#586D52]">
                              {getInitials(candidate.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold truncate">{candidate.full_name || 'Unknown'}</h3>
                              <Badge variant="outline" className="capitalize text-xs">{candidate.role}</Badge>
                            </div>
                            
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                <span className="truncate max-w-[150px]">{candidate.email}</span>
                              </span>
                              {candidate.state && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {candidate.state}
                                </span>
                              )}
                            </div>
                            
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground">{completedCount}/{allOnboardingSteps.length} steps</span>
                                <span className="font-medium">{progress}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div className="h-full bg-[#586D52] transition-all" style={{ width: `${progress}%` }} />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {onb.editCandidate && (
                              <Button variant="ghost" size="sm" onClick={() => setEditingCandidate(candidate)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setExpandedCandidates(prev => ({...prev, [candidate.id]: !prev[candidate.id]}))}>
                              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs font-medium text-muted-foreground mb-2">Onboarding Progress</p>
                            <div className="space-y-1.5">
                              {allOnboardingSteps.map((step) => {
                                const isComplete = candidate[step.key];
                                const completedAt = candidate[`${step.key}_at`];
                                const wasAdminOverride = candidate[`${step.key}_admin_override`];
                                
                                return (
                                  <div 
                                    key={step.key}
                                    className={`flex items-center justify-between p-2 rounded ${isComplete ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-800'}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-5 h-5 rounded flex items-center justify-center ${isComplete ? 'bg-green-500 text-white' : 'border border-gray-300 dark:border-gray-600'}`}>
                                        {isComplete && <CheckCircle2 className="w-3 h-3" />}
                                      </div>
                                      <span className={`text-xs ${isComplete ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                        {step.label}
                                      </span>
                                      {wasAdminOverride && <Badge variant="outline" className="text-[9px] px-1">Admin</Badge>}
                                    </div>
                                    {isComplete && completedAt && (
                                      <span className="text-[10px] text-muted-foreground">{formatDate(completedAt)}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ADMIN CREATIONS TAB */}
        {mainTab === 'admin_creations' && (
          <div className="space-y-4" data-testid="admin-creations-section">
            <p className="text-sm text-muted-foreground mb-2">
              Every account you (or another admin) provisioned manually
              via the Create-User dialog. Inspect their onboarding
              progress, edit their profile, or override individual
              steps just like in the Candidates tab.
            </p>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name or email…"
                defaultValue={searchQuery}
                onInput={(e) => {
                  const value = e.target.value;
                  if (window.searchTimeout) clearTimeout(window.searchTimeout);
                  window.searchTimeout = setTimeout(() => setSearchQuery(value), 100);
                }}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="admin-creation-search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>

            {(() => {
              const q = (searchQuery || '').toLowerCase();
              const list = adminCreations.filter((u) => {
                if (!q) return true;
                return (
                  (u.full_name || '').toLowerCase().includes(q) ||
                  (u.email || '').toLowerCase().includes(q) ||
                  (u.first_name || '').toLowerCase().includes(q) ||
                  (u.last_name || '').toLowerCase().includes(q)
                );
              });
              if (loading) {
                return (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Card key={i} className="animate-pulse">
                        <CardContent className="p-4 h-24 bg-muted/50" />
                      </Card>
                    ))}
                  </div>
                );
              }
              if (list.length === 0) {
                return (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <User className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-muted-foreground">
                        {searchQuery ? 'No admin-created users match your search' : 'No users have been created via the admin Create-User flow yet.'}
                      </p>
                    </CardContent>
                  </Card>
                );
              }
              return (
                <div className="space-y-3">
                  {list.map((u) => {
                    const progress = getCandidateProgress(u);
                    const completedCount = allOnboardingSteps.filter((s) => u[s.key]).length;
                    const isExpanded = expandedCandidates[u.id];
                    return (
                      <Card key={u.id} className="hover:shadow-md transition-all" data-testid={`admin-creation-card-${u.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Avatar className="h-12 w-12 flex-shrink-0">
                              <AvatarFallback className="bg-[#586D52]/20 text-[#586D52]">
                                {getInitials(u.full_name)}
                              </AvatarFallback>
                            </Avatar>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold truncate">{u.full_name || 'Unknown'}</h3>
                                <Badge variant="outline" className="capitalize text-xs">{u.title || u.role}</Badge>
                                {u.is_onboarded && (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Onboarded</Badge>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate max-w-[200px]">{u.email}</span>
                                </span>
                                {u.state && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {u.state}
                                  </span>
                                )}
                                {u.created_at && (
                                  <span>Created {formatDate(u.created_at)}</span>
                                )}
                              </div>

                              <div className="mt-2">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">{completedCount}/{allOnboardingSteps.length} steps</span>
                                  <span className="font-medium">{progress}%</span>
                                </div>
                                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#586D52] transition-all" style={{ width: `${progress}%` }} />
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button variant="ghost" size="sm" onClick={() => setEditingCandidate(u)} data-testid={`edit-admin-creation-${u.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setExpandedCandidates((prev) => ({...prev, [u.id]: !prev[u.id]}))}>
                                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </Button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                              <p className="text-xs font-medium text-muted-foreground mb-2">Onboarding Progress</p>
                              <div className="space-y-1.5">
                                {allOnboardingSteps.map((step) => {
                                  const isComplete = u[step.key];
                                  const completedAt = u[`${step.key}_at`];
                                  const wasAdminOverride = u[`${step.key}_admin_override`];
                                  return (
                                    <div
                                      key={step.key}
                                      className={`flex items-center justify-between p-2 rounded ${isComplete ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-800'}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <div className={`w-5 h-5 rounded flex items-center justify-center ${isComplete ? 'bg-green-500 text-white' : 'border border-gray-300 dark:border-gray-600'}`}>
                                          {isComplete && <CheckCircle2 className="w-3 h-3" />}
                                        </div>
                                        <span className={`text-xs ${isComplete ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                          {step.label}
                                        </span>
                                        {wasAdminOverride && <Badge variant="outline" className="text-[9px] px-1">Admin</Badge>}
                                      </div>
                                      {isComplete && completedAt && (
                                        <span className="text-[10px] text-muted-foreground">{formatDate(completedAt)}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingAdminPage;
